import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, Plus, Search, Upload } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { NewMaterialModal } from './NewMaterialModal'
import { MaterialImportWizard } from './MaterialImportWizard'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MaterialsTable, STATUS_LABELS } from './MaterialsTable'
import { ProfilePopup } from './ProfilePopup'
import { useDeleteMaterial, useMaterials } from '@/hooks/useInventoryMaster'
import { inventoryApi } from '@/api/inventoryApi'
import { buildTemplateWorkbook } from '@/lib/materialImport'
import { usePermission } from '@/lib/permissions'
import { useTenant } from '@/providers/TenantProvider'
import type {
  ApiMaterial,
  InventoryProfileType,
  MaterialStatus,
  MaterialStockFilter,
} from '@/types/inventory'

const ALL = 'all'
const PAGE_SIZE = 25

const STATUS_OPTIONS: MaterialStatus[] = [
  'available',
  'reserved',
  'in_transit',
  'quarantine',
  'hold',
  'retired',
  'in_process',
]

/** The re-order rule's view of stock (z8nrz7f7dz), beside the status filter. */
const STOCK_OPTIONS: { value: MaterialStockFilter; label: string }[] = [
  { value: 'low', label: 'Low Stock' },
  { value: 'out', label: 'Out of Stock' },
  { value: 'expired', label: 'Expired' },
]

interface MasterDataTabProps {
  /** When set, only materials of this type are shown (Consumables / Reagents / Kits tabs). */
  materialType?: string
}

/** The profile-driven material master. Shared by the Master Data / Consumables / Reagents / Kits tabs. */
export function MasterDataTab({ materialType }: MasterDataTabProps) {
  const [search, setSearch] = useState('')
  // `?q=` searches the table, as a link to one material does (z8nrz7fkfh).
  // Read once and cleared, so the box is the user's again after.
  const [params, setParams] = useSearchParams()
  const linkedSearch = params.get('q')
  useEffect(() => {
    if (linkedSearch === null) return
    setSearch(linkedSearch)
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('q')
        return next
      },
      { replace: true },
    )
  }, [linkedSearch, setParams])
  const [status, setStatus] = useState<string>(ALL)
  const [stock, setStock] = useState<string>(ALL)
  const [page, setPage] = useState(1)
  const [popup, setPopup] = useState<{ material: ApiMaterial; profileType: InventoryProfileType } | null>(
    null,
  )
  const [showNew, setShowNew] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [buildingTemplate, setBuildingTemplate] = useState(false)
  const [templateError, setTemplateError] = useState('')
  const { can } = usePermission()
  const { selectedTenantId } = useTenant()
  const canImport = can('inventory.import')

  /**
   * Fetch the schema and hand back a workbook shaped like it.
   *
   * Built here rather than served by the backend: the column colours are
   * frontend design tokens, and an endpoint that returned a styled workbook
   * would need its own copy of them.
   */
  async function downloadTemplate() {
    if (!selectedTenantId) return
    setBuildingTemplate(true)
    setTemplateError('')
    try {
      const schema = await inventoryApi.getImportSchema(selectedTenantId)
      const buffer = await buildTemplateWorkbook(schema)
      const url = URL.createObjectURL(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      )
      const link = document.createElement('a')
      link.href = url
      link.download = 'material-import-template.xlsx'
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setTemplateError(
        err instanceof Error ? err.message : 'The template could not be prepared.',
      )
    } finally {
      setBuildingTemplate(false)
    }
  }

  const del = useDeleteMaterial()

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    setPage(1)
  }, [search, status, stock, materialType])

  const { data, isLoading, error } = useMaterials({
    type: materialType,
    status: status === ALL ? undefined : status,
    stock: stock === ALL ? undefined : (stock as MaterialStockFilter),
    q: search.trim() || undefined,
    page,
    pageSize: PAGE_SIZE,
  })

  const rows = data?.items ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  function handleDelete(material: ApiMaterial) {
    if (window.confirm(`Delete material ${material.materialId} (${material.materialName})?`)) {
      del.mutate(material.id)
    }
  }

  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
      <div className='flex flex-wrap items-center gap-3 border-b border-gray-100 p-4'>
        <div className='relative min-w-[240px] flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
          {/* Autofill guards, not decoration. A password manager pairing the
            * delete-lot signature dialog's password field with a "username"
            * hunts the whole page for a text input, and this one — the first
            * on the screen — was getting the signed-in user's email. It is
            * controlled, so the list silently filtered itself to 0 materials
            * behind the open dialog. SignatureDialog's own decoy is the fix
            * that keeps the guess inside the dialog; these make this box an
            * unattractive target even if a manager ignores it. */}
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search material ID, code, name…'
            className='pl-9'
            name='material-search'
            autoComplete='off'
            data-1p-ignore
            data-lpignore='true'
            data-form-type='other'
          />
        </div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className='w-[160px] shrink-0'>
            <SelectValue placeholder='All statuses' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {/* The shared label map, not a de-underscored enum value: deriving
                the text here produced "in process" beside a badge reading "In
                Progress" for the same status. One name per status, in one
                place. */}
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s] ?? s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={stock} onValueChange={setStock}>
          <SelectTrigger className='w-[150px] shrink-0' aria-label='Stock filter'>
            <SelectValue placeholder='All stock' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All stock</SelectItem>
            {STOCK_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {canImport && (
          <>
            <Button
              variant='outline'
              className='gap-1'
              disabled={buildingTemplate}
              onClick={() => void downloadTemplate()}
            >
              <Download className='h-4 w-4' />
              {buildingTemplate ? 'Preparing…' : 'Download template'}
            </Button>
            <Button variant='outline' className='gap-1' onClick={() => setShowImport(true)}>
              <Upload className='h-4 w-4' />
              Import
            </Button>
          </>
        )}

        <Button className='gap-1' onClick={() => setShowNew(true)}>
          <Plus className='h-4 w-4' />
          Add material
        </Button>
      </div>

      {templateError && (
        <p className='px-4 pb-2 text-sm text-red-600'>{templateError}</p>
      )}

      <div className='p-4'>
        {isLoading && (
          <p className='py-12 text-center text-sm text-muted-foreground'>Loading materials…</p>
        )}
        {error && (
          <p className='py-12 text-center text-sm text-red-600'>
            {error instanceof Error ? error.message : 'Failed to load materials.'}
          </p>
        )}
        {!isLoading && !error && (
          <>
            <MaterialsTable
              rows={rows}
              onOpenProfile={(material, profileType) => setPopup({ material, profileType })}
              onDelete={handleDelete}
            />
            {rows.length === 0 && (
              <p className='py-12 text-center text-sm text-muted-foreground'>
                No materials match these filters.
              </p>
            )}

            <div className='mt-4 flex items-center justify-between'>
              <p className='text-xs text-gray-500'>
                {total} material{total === 1 ? '' : 's'}
              </p>
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <span className='text-xs text-gray-500'>
                  Page {page} / {pageCount}
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <ProfilePopup
        material={popup?.material ?? null}
        profileType={popup?.profileType ?? null}
        open={popup !== null}
        onClose={() => setPopup(null)}
      />

      <NewMaterialModal open={showNew} onClose={() => setShowNew(false)} />
      <MaterialImportWizard open={showImport} onClose={() => setShowImport(false)} />
    </div>
  )
}
